import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send } from "lucide-react";
import { api } from "../lib/api";

const GREETING = "Olá! 👋 Sou o assistente do STAND LF. Em que posso ajudar? (contactos, morada, horário, financiamento…)";

export const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: "greeting", role: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const idRef = useRef(1);
  const nextId = () => String(idRef.current++);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, loading]);

  // hide on admin/login pages
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/login")) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { id: nextId(), role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text });
      setMessages((m) => [...m, { id: nextId(), role: "bot", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { id: nextId(), role: "bot", text: "Desculpe, ocorreu um erro. Contacte-nos pelo 924 020 938." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-toggle"
          onClick={() => setOpen(true)}
          aria-label="Abrir assistente"
          className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/40 flex items-center justify-center transition-transform hover:scale-105 focus-visible:ring-4 focus-visible:ring-primary/30 focus-visible:outline-none"
        >
          <MessageSquare className="w-7 h-7" />
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px] h-[540px] max-h-[80vh] bg-white rounded-3xl shadow-2xl border border-black/10 flex flex-col overflow-hidden animate-fade-up">
          {/* header */}
          <div className="bg-zinc-950 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center font-heading font-black text-primary-foreground">LF</span>
              <div className="leading-tight">
                <p className="font-heading font-bold text-sm">Assistente STAND LF</p>
                <p className="text-[11px] text-white/60 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" /> Online</p>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Fechar" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/30">
            {messages.map((m) => (
              <div key={m.id} data-testid="chat-message" className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-white border border-black/5 text-foreground rounded-bl-md"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-black/5 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* input */}
          <div className="p-3 border-t border-black/5 bg-white flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Escreva a sua mensagem..."
              className="flex-1 h-11 px-4 rounded-full bg-secondary/60 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <button
              data-testid="chat-send"
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Enviar"
              className="w-11 h-11 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-transform hover:scale-105"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
